import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { BadgeCheck, Clock, Eye, FileCheck2, MessageSquareWarning, Trash2, UploadCloud } from "lucide-react";
import SecureDocViewer from "@/components/app/SecureDocViewer";

export function ApprovalBadge({ status, testid }) {
  if (status === "Client Approved") {
    return <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] font-mono-tight uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5 rounded"><BadgeCheck size={11} /> Client Approved</span>;
  }
  if (status === "Changes Requested") {
    return <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] font-mono-tight uppercase tracking-widest bg-orange-100 text-orange-800 border border-orange-300 px-1.5 py-0.5 rounded"><MessageSquareWarning size={11} /> Changes Requested</span>;
  }
  if (status === "Awaiting Approval") {
    return <span data-testid={testid} className="inline-flex items-center gap-1 text-[10px] font-mono-tight uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded"><Clock size={11} /> Awaiting Client Approval</span>;
  }
  return null;
}

export default function ClientApprovalPanel({ project, canUpload, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const files = project.approval_files || [];
  const lastReq = (project.change_requests || []).slice(-1)[0];

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/projects/${project.id}/approval-files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} shared with the client for approval`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove "${f.name}" from Client Approval?`)) return;
    try { await api.delete(`/projects/${project.id}/approval-files/${f.id}`); onChanged(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const approved = project.client_approval_status === "Client Approved";
  return (
    <div className={`border bg-white rounded-2xl lg:col-span-3 ${approved ? "border-emerald-500" : "border-black/10"}`} data-testid="client-approval-panel">
      <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between gap-3 flex-wrap">
        <div className="label-caps flex items-center gap-2 text-royal-800"><FileCheck2 size={12} /> Client Approval</div>
        <div className="flex items-center gap-3">
          <ApprovalBadge status={project.client_approval_status} testid="pd-approval-status" />
          {approved && <span className="text-xs text-neutral-600 font-mono-tight" data-testid="pd-approved-at">{project.client_approved_by} · {new Date(project.client_approved_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>}
          {canUpload && (
            <>
              <button data-testid="approval-upload-btn" disabled={busy} onClick={() => inputRef.current?.click()} className="bg-black text-white px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
                <UploadCloud size={13} /> {busy ? "Uploading…" : "Upload for approval"}
              </button>
              <input ref={inputRef} data-testid="approval-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" hidden onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
            </>
          )}
        </div>
      </div>
      <div className="p-4">
        <p className="text-xs text-neutral-500 mb-3">Files here appear instantly in the client's portal as a view-only preview with your logo and a DRAFT watermark. PDF or image, up to 25 MB.</p>
        {project.client_approval_status === "Changes Requested" && lastReq && (
          <div className="mb-3 border border-orange-300 bg-orange-50 rounded-lg px-3 py-2.5 text-sm text-orange-900" data-testid="pd-change-request">
            <div className="font-semibold flex items-center gap-1.5"><MessageSquareWarning size={14} /> {lastReq.by} requested changes on “{lastReq.file_name}”</div>
            <div className="mt-1 whitespace-pre-wrap" data-testid="pd-change-request-comment">{lastReq.comment}</div>
            <div className="text-[11px] font-mono-tight text-orange-800/70 mt-1">{new Date(lastReq.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · Upload a revised draft to send it back for approval.</div>
          </div>
        )}
        <ul className="space-y-2" data-testid="approval-files-list">
          {files.map((f) => (
            <li key={f.id} data-testid={`approval-file-${f.id}`} className="flex items-center justify-between border border-neutral-200 rounded px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{f.name}</div>
                <div className="text-xs text-neutral-500 font-mono-tight">by {f.uploaded_by} · {formatDate(f.uploaded_at)} · {f.page_count} page{f.page_count === 1 ? "" : "s"}</div>
              </div>
              <div className="flex items-center gap-2 ml-3">
                <button data-testid={`approval-preview-${f.id}`} onClick={() => setViewing(f)} className="text-xs underline underline-offset-4 inline-flex items-center gap-1"><Eye size={13} /> Preview as client</button>
                {canUpload && <button data-testid={`approval-remove-${f.id}`} onClick={() => remove(f)} className="text-[#B02A00]"><Trash2 size={14} /></button>}
              </div>
            </li>
          ))}
          {files.length === 0 && <li className="text-sm text-neutral-500 text-center py-4">No files shared for client approval yet.</li>}
        </ul>
      </div>
      {viewing && <SecureDocViewer client={api} file={viewing} onClose={() => setViewing(null)}
        pageUrl={(n) => `/projects/${project.id}/approval-files/${viewing.id}/pages/${n}`} />}
    </div>
  );
}
